import { Box, Text, Link } from '@ltvco/refresh-lib/theme';
import React from 'react';

import {
  ORIGIN_MAP,
  JURISDICTION_MAP,
  NATURE_OF_SUIT_MAP,
  DISPOSITION_MAP,
  PRO_SE_MAP,
} from '../../../constants';
import { CourtDocket } from '../../../types';
import { formatCourtDateWithTime } from '../../../utils';
import {
  DocketEntriesContainer,
  TableHeader,
  HeaderCellText,
  HeaderCellDescription,
  Divider,
  TableRow,
} from '../DocketEntries/components';

import {
  IdbHeaderCell,
  IdbCellWrapper,
  FieldNameCell,
  ValueCell,
  HintsCell,
} from './components';

interface IdbDataTableProps {
  docketData: CourtDocket;
}

interface IdbDataField {
  key: string;
  label: string;
  description: string | React.ReactNode;
  value: any;
}

const getDescriptionFromMap = (
  value: any,
  map: { [key: string]: string }
): string => {
  return map[String(value)] || value;
};

const getValueOrNotProvided = (value: unknown): string | number | boolean =>
  value === null || value === undefined || value === ''
    ? 'Not Provided'
    : (value as string | number | boolean);

const IDB_FIELD_MAPPING: Array<{
  key: string;
  label: string;
  description: string | React.ReactNode;
  valueExtractor: (data: any) => any;
}> = [
  {
    key: 'office',
    label: 'Office Number',
    description: (
      <>
        Office codes are listed in the{' '}
        <Link
          href="https://free.law/xlsx/fjc/integrated-database/office-codes.xlsx"
          target="_blank"
          rel="noopener noreferrer"
        >
          Guide to Judiciary Policies and Procedures, Appendix A.
        </Link>
      </>
    ),
    valueExtractor: (data) => getValueOrNotProvided(data.office),
  },
  {
    key: 'origin',
    label: 'Origin',
    description:
      "Possible values: Original Proceeding; Removed (began in the state court, removed to the district court); Remanded for further action (removal from court of appeals); Reinstated/reopened (previously opened and closed, reopened for additional action); Transferred from another district(pursuant to 28 USC 1404); Multi district litigation (cases transferred to this district by an order entered by Judicial Panel on Multi District Litigation pursuant to 28 USC 1407); Appeal to a district judge of a magistrate judge's decision; Second reopen; Third reopen; Fourth reopen; Fifth reopen; Sixth reopen; or Multi district litigation originating in the district (valid beginning July 1, 2016).",
    valueExtractor: (data) =>
      data.origin !== null && data.origin !== undefined
        ? getDescriptionFromMap(data.origin, ORIGIN_MAP)
        : 'Not Provided',
  },
  {
    key: 'date_filed',
    label: 'Date Filed',
    description: '',
    valueExtractor: (data) =>
      data.date_filed
        ? formatCourtDateWithTime(data.date_filed)
        : 'Not Provided',
  },
  {
    key: 'date_terminated',
    label: 'Date Terminated',
    description: '',
    valueExtractor: (data) =>
      data.date_terminated
        ? formatCourtDateWithTime(data.date_terminated)
        : 'Not Provided',
  },
  {
    key: 'jurisdiction',
    label: 'Jurisdiction',
    description:
      'The basis for U.S. district court jurisdiction in the case. Possible values: Government plaintiff; Government defendant; Federal question; Diversity of citizenship; or Local question.',
    valueExtractor: (data) =>
      data.jurisdiction !== null && data.jurisdiction !== undefined
        ? getDescriptionFromMap(data.jurisdiction, JURISDICTION_MAP)
        : 'Not Provided',
  },
  {
    key: 'nature_of_suit',
    label: 'Nature of Suit',
    description: '',
    valueExtractor: (data) =>
      data.nature_of_suit !== null && data.nature_of_suit !== undefined
        ? getDescriptionFromMap(data.nature_of_suit, NATURE_OF_SUIT_MAP)
        : 'Not Provided',
  },
  {
    key: 'title',
    label: 'Title',
    description: 'The title in the U.S.C. for the cause of this case.',
    valueExtractor: (data) => getValueOrNotProvided(data.title),
  },
  {
    key: 'section',
    label: 'Section',
    description: 'The section in the U.S.C. for the cause of this case.',
    valueExtractor: (data) => getValueOrNotProvided(data.section),
  },
  {
    key: 'subsection',
    label: 'Subsection',
    description: 'The subsection in the U.S.C. for the cause of this case.',
    valueExtractor: (data) => getValueOrNotProvided(data.subsection),
  },
  {
    key: 'diversity_of_residence',
    label: 'Diversity of Residence',
    description: '',
    valueExtractor: (data) =>
      getValueOrNotProvided(data.diversity_of_residence),
  },
  {
    key: 'class_action',
    label: 'Class Action Status',
    description:
      'If true, designates that the case involves an allegation by the plaintiff that the complaint meets the prerequisites of "Class Action" as provided in Rule 23.',
    valueExtractor: (data) =>
      data.class_action === true
        ? 'Yes'
        : data.class_action === false
        ? 'No'
        : 'Not Provided',
  },
  {
    key: 'termination_class_action_status',
    label: 'Termination Class Action Status',
    description: 'Possible values: Denied or Granted.',
    valueExtractor: (data) =>
      getValueOrNotProvided(data.termination_class_action_status),
  },
  {
    key: 'monetary_demand',
    label: 'Monetary Demand',
    description: (
      <>
        The monetary amount sought by the plaintiff (in thousands). Amounts less
        than $500 appear as 1 and amounts over $10k appear as 9999. See notes in{' '}
        <Link
          href="https://www.fjc.gov/research/idb/"
          target="_blank"
          rel="noopener noreferrer"
        >
          codebook on FJC website
        </Link>{' '}
        for details.
      </>
    ),
    valueExtractor: (data) =>
      data.monetary_demand !== null && data.monetary_demand !== undefined
        ? String(data.monetary_demand)
        : 'Not Provided',
  },
  {
    key: 'county_of_residence',
    label: 'County of Residence',
    description: (
      <>
        The code for the county of residence for the first listed plaintiff (see
        notes in{' '}
        <Link
          href="https://www.fjc.gov/research/idb/"
          target="_blank"
          rel="noopener noreferrer"
        >
          codebook on FJC website
        </Link>
        ).
      </>
    ),
    valueExtractor: (data) => getValueOrNotProvided(data.county_of_residence),
  },
  {
    key: 'arbitration_at_filing',
    label: 'Arbitration at Filing',
    description:
      'Only used by courts participating in the Formal Arbitration Program. Possible values: Mandatory; Voluntary; Exempt; or Yes, but type unknown.',
    valueExtractor: (data) => getValueOrNotProvided(data.arbitration_at_filing),
  },
  {
    key: 'arbitration_at_termination',
    label: 'Arbitration at Termination',
    description:
      'Possible values: Mandatory; Voluntary; Exempt; or Yes, but type unknown.',
    valueExtractor: (data) =>
      getValueOrNotProvided(data.arbitration_at_termination),
  },
  {
    key: 'multidistrict_litigation_docket_number',
    label: 'Multidistrict Litigation Number',
    description: 'A four digit multidistrict litigation number.',
    valueExtractor: (data) =>
      getValueOrNotProvided(data.multidistrict_litigation_docket_number),
  },
  {
    key: 'plaintiff',
    label: 'Plaintiff',
    description:
      'The first listed plaintiff in the case. Field truncated to 30 characters upon extraction by the AOUSC source system.',
    valueExtractor: (data) => getValueOrNotProvided(data.plaintiff),
  },
  {
    key: 'defendant',
    label: 'Defendant',
    description:
      'The first listed defendant in the case. Field truncated to 30 characters upon extraction by the AOUSC source system.',
    valueExtractor: (data) => getValueOrNotProvided(data.defendant),
  },
  {
    key: 'date_transfer',
    label: 'Date of Transfer',
    description:
      'The date when the papers for the case were received by the receiving district for a transferred case.',
    valueExtractor: (data) =>
      data.date_transfer
        ? formatCourtDateWithTime(data.date_transfer)
        : 'Not Provided',
  },
  {
    key: 'transfer_docket_number',
    label: 'Transfer Docket Number',
    description: 'The docket number of the case in the source district.',
    valueExtractor: (data) =>
      getValueOrNotProvided(data.transfer_docket_number),
  },
  {
    key: 'transfer_origin',
    label: 'Transfer Origin',
    description: 'The origin number of the case in the source district.',
    valueExtractor: (data) => getValueOrNotProvided(data.transfer_origin),
  },
  {
    key: 'procedural_progress',
    label: 'Procedural Progress',
    description: (
      <>
        The point to which the case had progressed when it was disposed of (see
        notes in{' '}
        <Link
          href="https://www.fjc.gov/research/idb/"
          target="_blank"
          rel="noopener noreferrer"
        >
          codebook on FJC website
        </Link>
        ). Possible values: No court action (before issue joined); Order
        entered; Hearing held; Order decided; No court action (after issue
        joined); Judgment on motion; Pretrial conference held; During court
        trial; During jury trial; After court trial; After jury trial; Other; or
        Request for trial de novo after arbitration.
      </>
    ),
    valueExtractor: (data) => getValueOrNotProvided(data.procedural_progress),
  },
  {
    key: 'disposition',
    label: 'Disposition',
    description:
      'The manner in which the case was disposed of. Possible values: Transfer to another district; Remanded to state court; Multi-district litigation transfer; Remanded to U.S. agency; Want of prosecution; Lack of jurisdiction; Voluntarily dismissed; Settled; Other; Default; Consent; Motion before trial; Jury verdict; Directed verdict; Court trial; Award of arbitrator; Stayed pending bankruptcy; Other; Statistical closing; Appeal affirmed (magistrate judge); or Appeal denied (magistrate judge).',
    valueExtractor: (data) =>
      data.disposition !== null && data.disposition !== undefined
        ? getDescriptionFromMap(data.disposition, DISPOSITION_MAP)
        : 'Not Provided',
  },
  {
    key: 'nature_of_judgement',
    label: 'Nature of Judgment',
    description:
      'Cases disposed of by an entry of a final judgment. Possible values: No monetary award; Monetary award only; Monetary award and other; Injunction; Forfeiture/foreclosure/condemnation, etc.; Costs only; or Costs and attorney fees.',
    valueExtractor: (data) => getValueOrNotProvided(data.nature_of_judgement),
  },
  {
    key: 'amount_received',
    label: 'Amount Received',
    description: (
      <>
        Dollar amount received (in thousands) when appropriate. Field not
        uniformly used;{' '}
        <Link
          href="https://www.fjc.gov/research/idb/"
          target="_blank"
          rel="noopener noreferrer"
        >
          see codebook
        </Link>
        .
      </>
    ),
    valueExtractor: (data) =>
      data.amount_received !== null && data.amount_received !== undefined
        ? String(data.amount_received)
        : 'Not Provided',
  },
  {
    key: 'judgment',
    label: 'Judgment',
    description:
      'Which party the case was disposed in favor of. Possible values: Plaintiff; Defendant; Both plaintiff and defendant; or Unknown.',
    valueExtractor: (data) => getValueOrNotProvided(data.judgment),
  },
  {
    key: 'pro_se',
    label: 'Pro Se',
    description: (
      <>
        The parties that filed pro se in the case.{' '}
        <Link
          href="https://www.fjc.gov/research/idb/"
          target="_blank"
          rel="noopener noreferrer"
        >
          See codebook on IDB website
        </Link>{' '}
        for more details. Possible values: No pro se plaintiffs or defendants;
        Pro se plaintiffs, but no pro se defendants; Pro se defendants, but no
        pro se plaintiffs; or Both pro se plaintiffs & defendants.
      </>
    ),
    valueExtractor: (data) =>
      data.pro_se !== null && data.pro_se !== undefined
        ? getDescriptionFromMap(data.pro_se, PRO_SE_MAP)
        : 'Not Provided',
  },
];

const idbDataToTableData = (idbData: any): IdbDataField[] => {
  if (!idbData || typeof idbData !== 'object') {
    return [];
  }

  return IDB_FIELD_MAPPING.map((fieldConfig) => ({
    key: fieldConfig.key,
    label: fieldConfig.label,
    description: fieldConfig.description,
    value: fieldConfig.valueExtractor(idbData),
  }));
};

export const IdbDataTable = ({ docketData }: IdbDataTableProps) => {
  const tableData = idbDataToTableData(docketData.idb_data);

  if (tableData.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Text variant="body2" color="text.secondary">
          IDB data is empty for this case.
        </Text>
      </Box>
    );
  }

  return (
    <DocketEntriesContainer>
      <TableHeader>
        <IdbHeaderCell>
          <HeaderCellText>Field</HeaderCellText>
        </IdbHeaderCell>
        <IdbHeaderCell>
          <HeaderCellText>Value</HeaderCellText>
        </IdbHeaderCell>
        <HeaderCellDescription>
          <HeaderCellText>Hints</HeaderCellText>
        </HeaderCellDescription>
      </TableHeader>

      <Divider />

      {tableData.map((field, index) => {
        const isAlternate = index % 2 === 1;
        return (
          <Box key={field.key}>
            <TableRow isAlternate={isAlternate}>
              <IdbCellWrapper>
                <FieldNameCell>{field.label}</FieldNameCell>
                <ValueCell>{field.value}</ValueCell>
                <HintsCell>
                  {typeof field.description === 'string' ? (
                    <Text
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        width: '100%',
                      }}
                    >
                      {field.description}
                    </Text>
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                      }}
                    >
                      {field.description}
                    </Box>
                  )}
                </HintsCell>
              </IdbCellWrapper>
            </TableRow>
            {index < tableData.length - 1 && <Divider />}
          </Box>
        );
      })}
    </DocketEntriesContainer>
  );
};
